import React from 'react';
import { getContext } from '@fiverr-private/fiverr_context';
import { Stack } from '@fiverr-private/layout_components';
import { fileSizeFormat } from '@fiverr-private/localization';
import { Text, type TextProps } from '@fiverr-private/typography';
import type { Types } from '@fiverr-private/theme';
import Asset from '../../Asset';
import { useActiveState } from '../../hooks/useActiveState';
import RemoveButton from '../RemoveButton';
import type { AttachmentProps } from '../types';
import { renderAttachmentsChildren } from '../../utils';

const AttachmentListItem = ({
  error,
  showPreview,
  onRemove,
  onClick,
  hasError,
  name,
  fileSize: fileSizeProp,
  url,
  children,
  variant,
  theme,
}: AttachmentProps) => {
  const { isTouch } = getContext();
  const { isActive, handleMouseEnter, handleMouseLeave, handleFocus, handleBlur } = useActiveState();
  const fileSize = fileSizeProp
    ? fileSizeFormat({
        bytes: fileSizeProp,
        options: {
          maximumFractionDigits: 0,
        },
      })
    : undefined;

  const ellipsisPros: TextProps = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  const getBackgroundColor = (isActive: boolean): Types.BackgroundColor | undefined => {
    if (theme === 'dark') {
      return isActive ? 'black_20' : 'black_10';
    }

    return isActive ? 'grey_100' : undefined;
  };

  return (
    <Stack
      gap="6"
      paddingY="1.5"
      paddingX="2"
      justifyContent="spaceBetween"
      borderRadius="sm"
      alignItems="center"
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      tabIndex={0}
      onClick={onClick}
      backgroundColor={getBackgroundColor(isActive)}
    >
      <Stack gap="4" overflow="hidden">
        <Asset name={name} url={url} hasError={hasError} variant={variant} showPreview={showPreview} theme={theme} />
        <Stack direction="column" gap="0" overflow="hidden">
          {!(isTouch && hasError) && (
            <Text color={theme === 'dark' ? 'white' : undefined} size="b_sm" fontWeight="semibold" {...ellipsisPros}>
              {name}
            </Text>
          )}
          <Text size="b_sm" color={hasError ? 'validationError' : 'bodySecondary'} {...ellipsisPros}>
            {hasError ? error : fileSize?.toLocaleUpperCase()}
          </Text>
          {hasError && isTouch && renderAttachmentsChildren(children, { variant, theme })}
        </Stack>
      </Stack>
      <Stack flexShrink={0} gap="2">
        {hasError && isActive && !isTouch && renderAttachmentsChildren(children, { variant, theme })}
        {(isActive || isTouch) && <RemoveButton variant={variant} onClick={onRemove} theme={theme} />}
      </Stack>
    </Stack>
  );
};

AttachmentListItem.displayName = 'AttachmentListItem';
export default AttachmentListItem;
